import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, MessageSquareQuote, Loader2, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

export default function FeedbackForm() {
  const [existing, setExisting] = useState(undefined);
  const [rating, setRating] = useState(5);
  const [roleTitle, setRoleTitle] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/testimonials/me").then((r) => setExisting(r.data)).catch(() => setExisting(null));
  }, []);

  const submit = async () => {
    if (!roleTitle.trim() || message.trim().length < 10) {
      toast.error("Please add your role and at least a short message");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/testimonials/submit", { role_title: roleTitle, rating, message });
      setExisting(data);
      toast.success("Thanks for sharing your feedback!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  if (existing === undefined) return null;

  if (existing) {
    return (
      <div className="glass-panel rounded-2xl p-6 mt-6 text-center" data-testid="feedback-already-submitted">
        <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
        <p className="text-sm text-white font-medium">Thanks for your feedback!</p>
        <p className="text-xs text-slate-400 mt-1">
          Status: <span className="capitalize font-semibold">{existing.status}</span>
          {existing.status === "pending" && " — our team will review it shortly."}
          {existing.status === "approved" && " — it's now live on our landing page."}
        </p>
      </div>
    );
  }

  return (
    <div className="glass-panel rounded-2xl p-6 mt-6" data-testid="feedback-form">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquareQuote className="w-4 h-4 text-indigo-400" />
        <h3 className="font-heading font-semibold text-white">Share Your Success Story</h3>
      </div>
      <p className="text-xs text-slate-400 mb-4">Your feedback could be featured on our landing page to inspire the next batch of learners.</p>

      <div className="flex items-center gap-1 mb-4" data-testid="feedback-rating-stars">
        {Array.from({ length: 5 }).map((_, i) => (
          <button key={i} type="button" onClick={() => setRating(i + 1)} data-testid={`feedback-star-${i + 1}`}>
            <Star className={`w-6 h-6 ${i < rating ? "text-amber-400 fill-amber-400" : "text-slate-600"}`} />
          </button>
        ))}
      </div>

      <input
        value={roleTitle}
        onChange={(e) => setRoleTitle(e.target.value)}
        placeholder="Your role/designation (e.g. Salesforce Admin at TCS)"
        data-testid="feedback-role-input"
        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white mb-3 focus:outline-none focus:ring-1 focus:ring-indigo-400"
      />
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Tell us about your experience with the challenge..."
        rows={3}
        data-testid="feedback-message-input"
        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white mb-3 focus:outline-none focus:ring-1 focus:ring-indigo-400"
      />
      <button
        onClick={submit}
        disabled={submitting}
        data-testid="feedback-submit-button"
        className="flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
      >
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
        Submit Feedback
      </button>
    </div>
  );
}
